import { PayloadAction } from '@reduxjs/toolkit';
import {
  userLogin,
  userSocialLogin,
  IUserLoginResponse,
  fetchUserById,
  userLogout,
  impersonateUser,
  twoFactorAuthLogin,
  fetchWhoAmI,
  userSocialSignUpPro,
  IUserLoginErrorResponse,
  repdatatUserSingleSignOnLogin,
  preLoginCheck
} from './login.thunks';
import { AuthState, LoggingMode } from './authState';
import { IUser } from '../users/user';
import { userSignUp, userSocialSignUp } from '../signup/signup.thunks';
import { HttpCallStatus, ILoginOptionsResponse } from './login.thunk.interfaces';
import { Features, isFeatureEnabled } from '../../featureToggles/features';

const userLoginPendingReducer = (state: AuthState): void => {
  state.loading = true;
  state.error = null;
  state.isLoggedIn = false;
  state.loggingMode = LoggingMode.None;
  state.showReCaptchaV2ForLogin = false;
};

const userLoginFulfilledReducer = (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
  state.token = action.payload.token;
  state.userId = action.payload.userId;
  state.payloadType = action.payload.payloadType;
  state.hash = action.payload.hash;
  state.isLoggedIn = true;
  state.loading = false;
  state.loggingMode = LoggingMode.Credentials;
  state.error = null;
  state.showReCaptchaV2ForLogin = false;
};

const userSignUpRejectedReducer = (state: AuthState, action: PayloadAction<string>): void => {
  state.loading = false;
  state.error = action.payload;
  state.loggingMode = LoggingMode.Credentials;
};

const userLoginRejectedReducer = (state: AuthState, action: PayloadAction<IUserLoginErrorResponse>): void => {
  const { message, recaptchaError } = action.payload;
  state.loading = false;
  state.error = message;
  state.loggingMode = LoggingMode.Credentials;
  state.showReCaptchaV2ForLogin = !!recaptchaError;
};

const userSocialLoginPendingReducer = (state: AuthState): void => {
  state.loading = true;
  state.error = null;
  state.isLoggedIn = false;
  state.loggingMode = LoggingMode.None;
};

const userSocialLoginFulfilledReducer = (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
  state.token = action.payload?.token;
  state.userId = action.payload?.userId;
  state.isLoggedIn = true;
  state.loading = false;
  state.loggingMode = LoggingMode.Social;
};

const userSocialLoginRejectedReducer = (state: AuthState, action: PayloadAction<string>): void => {
  state.loading = false;
  state.error = action.payload;
  state.loggingMode = LoggingMode.Social;
};

const reducers = {
  [userLogin?.pending.type]: userLoginPendingReducer,
  [userLogin?.fulfilled.type]: userLoginFulfilledReducer,
  [userLogin?.rejected.type]: userLoginRejectedReducer,
  [userSignUp?.pending.type]: userLoginPendingReducer,
  [userSignUp?.fulfilled.type]: userLoginFulfilledReducer,
  [userSignUp?.rejected.type]: userSignUpRejectedReducer,
  [userSocialLogin?.pending.type]: userSocialLoginPendingReducer,
  [userSocialLogin?.fulfilled.type]: userSocialLoginFulfilledReducer,
  [userSocialLogin?.rejected.type]: userSocialLoginRejectedReducer,
  [userSocialSignUp?.pending.type]: userSocialLoginPendingReducer,
  [userSocialSignUp?.fulfilled.type]: userSocialLoginFulfilledReducer,
  [userSocialSignUp?.rejected.type]: userSocialLoginRejectedReducer,
  [userSocialSignUpPro?.pending.type]: userSocialLoginPendingReducer,
  [userSocialSignUpPro?.fulfilled.type]: userSocialLoginFulfilledReducer,
  [userSocialSignUpPro?.rejected.type]: userSocialLoginRejectedReducer,
  [fetchUserById?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
  },
  [fetchUserById?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUser>): void => {
    state.user = action.payload;
    state.loading = false;
  },
  [fetchUserById?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  },
  [fetchWhoAmI?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
    if (isFeatureEnabled(Features.WorkosSSO)) {
      // TODO: SIG-20312 add tests
      state.fetchWhoAmIStatus = HttpCallStatus.Loading;
    }
  },
  [fetchWhoAmI?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUser>): void => {
    state.user = action.payload;
    state.loading = false;
    if(isFeatureEnabled(Features.WorkosSSO)) {
      // TODO: SIG-20312 add tests
      state.fetchWhoAmIStatus = HttpCallStatus.Succeeded;
    }
  },
  [fetchWhoAmI?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
    if (isFeatureEnabled(Features.WorkosSSO)) {
      // TODO: SIG-20312 add tests
      state.fetchWhoAmIStatus = HttpCallStatus.Failed;
    }
  },
  [userLogout?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
  },
  [userLogout?.fulfilled.type]: (state: AuthState): void => {
    state.loading = false;
    state.token = '';
    state.isLoggedIn = false;
    state.userId = null;
    state.user = null;
    state.uniqueSession = true;
    state.payloadType = null;
    state.hash = null;
    state.loggingMode = LoggingMode.None;
  },
  [userLogout?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  },
  [impersonateUser?.pending.type]: (state: AuthState): void => {
    state.loadingImpersonate = true;
  },
  [impersonateUser?.fulfilled.type]: (state: AuthState): void => {
    state.loadingImpersonate = false;
  },
  [impersonateUser?.rejected.type]: (state: AuthState): void => {
    state.loadingImpersonate = false;
  },
  [twoFactorAuthLogin?.pending.type]: (state: AuthState): void => {
    state.loading = true;
    state.error = null;
    state.isLoggedIn = false;
  },
  [twoFactorAuthLogin?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
    state.token = action.payload.token;
    state.userId = action.payload.userId;
    state.payloadType = action.payload.payloadType;
    state.hash = null;
    state.isLoggedIn = true;
    state.loading = false;
  },
  [twoFactorAuthLogin?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    state.loading = false;
    state.error = action.payload;
  },
  [repdatatUserSingleSignOnLogin?.pending.type]: (state: AuthState, action: PayloadAction<string>): void => {
    // TODO: SIG-20312 add tests
    state.repdataSSOLoginStatus = HttpCallStatus.Loading;
  },
  [repdatatUserSingleSignOnLogin?.rejected.type]: (state: AuthState, action: PayloadAction<string>): void => {
    // TODO: SIG-20312 add tests
    state.repdataSSOLoginStatus = HttpCallStatus.Failed;
  },
  [repdatatUserSingleSignOnLogin?.fulfilled.type]: (state: AuthState, action: PayloadAction<IUserLoginResponse>): void => {
    // TODO: SIG-20312 add tests
    state.repdataSSOLoginStatus = HttpCallStatus.Succeeded;

    // Some lines down do the "trick" so when doing refresh (from browser) the users is lot kicked out
    // instead a refresh is performed and the sessions is maintained
    state.token = action.payload.token;
    state.userId = action.payload.userId;
    state.payloadType = action.payload.payloadType;
    state.hash = null;
    state.isLoggedIn = true;
    state.loading = false;
  },
  [preLoginCheck?.fulfilled.type]: (state: AuthState, action: PayloadAction<ILoginOptionsResponse>): void => {
    // TODO: SIG-20312 add tests
    const { payload } = action;
    const { options } = payload;

    state.loginOptions = options;
  }
};

export default reducers;
